import { datetime } from '@/shared/date/date.config'
import { MagnifyingGlassIcon } from '@phosphor-icons/react'
import { forwardRef, useImperativeHandle } from 'react'
import { useEffect, useRef } from 'react'
import { useHookstate } from '@hookstate/core'
import { AppButton, AppTooltip } from '@/shared/ui'
import InfiniteScroll from 'react-infinite-scroller'
import {
  LabelContainer,
  Tag,
  Down,
  Icon,
  DropDown,
  Search,
  ItemList,
  Item,
  Label,
  Controls,
  infiniteScroll
} from './partials.component'
type SingleSelectProps = {
  icon?: JSX.Element
  iconRight?: JSX.Element
  disabled?: boolean
  addTag?: string
  searchPlaceholder?: string
  options: {
    label: string
    value: any
    icon?: () => JSX.Element
  }[]
  value?: string
  onChange?: (value: any) => void
  type: 'button' | 'label'
  buttonText?: string
  maxValueLength?: number
  bordered?: boolean
  width?: string
  withoutSearch?: boolean
  dropDownSameWidth?: boolean
  paginationConfig?: {
    loadData: (page: number) => void
    loadDataInProgress: boolean
    hasMore: boolean
    onSearch?: (value: string) => void
  }
}

export interface SearchableSingleSelectRef {
  clearSearchValue: () => void
}

export const getUtcOffset = (location: string): string => {
  const offsetMinutes: number = datetime().tz(location).utcOffset()
  const hours: number = Math.floor(Math.abs(offsetMinutes) / 60)
  const minutes: number = Math.abs(offsetMinutes) % 60
  const sign: string = offsetMinutes >= 0 ? '+' : '-'
  const formattedMinutes: string = minutes.toString().padStart(2, '0')
  const utcOffsetString: string = `UTC ${sign}${hours}:${formattedMinutes}`
  return utcOffsetString
}

const SearchableSingleSelect = forwardRef<SearchableSingleSelectRef, SingleSelectProps>(
  (
    {
      icon,
      addTag,
      searchPlaceholder,
      options,
      value,
      onChange,
      disabled,
      type,
      buttonText,
      maxValueLength,
      bordered,
      width,
      withoutSearch,
      dropDownSameWidth,
      paginationConfig,
      iconRight
    }: SingleSelectProps,
    ref
  ) => {
    const dropdownRef = useRef(null)
    const showDropdown = useHookstate<boolean>(false)
    const searchedValue = useHookstate<string>('')
    const filteredOptions = useHookstate<{ label: string; value: any; icon?: () => JSX.Element }[]>([])
    const infiniteScrollRef = useRef(null)

    const clearSearchValue = () => {
      searchedValue.set('')
    }

    useImperativeHandle(ref, () => ({
      clearSearchValue
    }))

    const debounceRef = useRef<NodeJS.Timeout | null>(null)

    const debounceSearch = (value: string) => {
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        if (infiniteScrollRef.current) infiniteScrollRef.current.pageLoaded = 0
        paginationConfig.onSearch(value)
      }, 300)
    }

    const onSearch = (value: string): void => {
      searchedValue.set(value)
      if (paginationConfig && paginationConfig.onSearch) {
        debounceSearch(value)
        return
      }

      const lowercaseValue = value.toLowerCase()
      const searchedItems =
        lowercaseValue === ''
          ? options
          : options.filter((element) => {
              return element.label.toLowerCase().includes(lowercaseValue)
            })
      filteredOptions.set(searchedItems)
    }
    const onSelect = (option: { label: string; value: any }): void => {
      if (onChange) onChange(option)
      showDropdown.set(false)
    }
    const onMouseDown = (event: any) => {
      //@ts-ignore
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        showDropdown.set(false)
      }
    }

    useEffect(() => {
      document.addEventListener('mousedown', onMouseDown)
      return () => {
        document.removeEventListener('mousedown', onMouseDown)
      }
    }, [])

    useEffect(() => {
      filteredOptions.set(options)
    }, [options])

    return (
      <article ref={dropdownRef} style={{ position: 'relative' }}>
        {type === 'label' && (
          <LabelContainer disabled={disabled} bordered={bordered} width={width}>
            <Label>
              {icon && <Icon>{icon}</Icon>}
              {maxValueLength && value.length > maxValueLength ? (
                <AppTooltip placement='topLeft' title={value}>
                  <p aria-label='selected-store'>{`${value.substring(0, maxValueLength)}...`}</p>
                </AppTooltip>
              ) : (
                <p aria-label='selected-store'>{value}</p>
              )}
            </Label>
            <Controls>
              {addTag && <Tag>{addTag}</Tag>}
              <Down onClick={() => showDropdown.set(!showDropdown.get())} aria-label='test-down' />
            </Controls>
          </LabelContainer>
        )}
        {type === 'button' && (
          <AppButton disabled={disabled} onClick={() => showDropdown.set(!showDropdown.get())}>
            {icon && <Icon>{icon}</Icon>}
            {buttonText}
            {iconRight && <Icon>{iconRight}</Icon>}
          </AppButton>
        )}
        <DropDown
          style={{ zIndex: 99999 }}
          show={showDropdown.get()}
          withoutSearch={withoutSearch}
          width={dropDownSameWidth ? width : '35.8rem'}
        >
          {!withoutSearch && (
            <Search>
              <input
                value={searchedValue.get()}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={searchPlaceholder}
              />
              <span>
                <MagnifyingGlassIcon />
              </span>
            </Search>
          )}
          {filteredOptions.get().length > 0 && (
            <ItemList withoutSearch={withoutSearch}>
              <>
                {!!paginationConfig && (
                  <InfiniteScroll
                    ref={infiniteScrollRef}
                    initialLoad={false}
                    pageStart={0}
                    loadMore={paginationConfig.loadData}
                    hasMore={paginationConfig.hasMore}
                    loadDataInProgress={paginationConfig.loadDataInProgress}
                    useWindow={false}
                    style={infiniteScroll}
                  >
                    {filteredOptions.get({ noproxy: true }).map((option, index) => (
                      <Item key={index} onClick={() => onSelect(option)}>
                        {option.icon && <Icon>{option.icon()}</Icon>}
                        {option.label}
                      </Item>
                    ))}
                  </InfiniteScroll>
                )}
                {!paginationConfig &&
                  filteredOptions.get({ noproxy: true }).map((option, index) => (
                    <Item key={index} onClick={() => onSelect(option)}>
                      {option.icon && <Icon>{option.icon()}</Icon>}
                      {option.label}
                    </Item>
                  ))}
              </>
            </ItemList>
          )}
        </DropDown>
      </article>
    )
  }
)

export { SearchableSingleSelect }
